import { useCallback, useEffect, useId, useMemo, useState, type FormEvent } from 'react'
import { api } from '../lib/api'
import { UAT_PREVIEW_ITEMS } from '../data/uat-preview'
import {
  UAT_CASE_RESULT_LABEL,
  UAT_CASE_RESULTS,
  UAT_LIMITS,
  UAT_STATUS_LABEL,
  UAT_STATUS_TONE,
  UAT_STATUSES,
  isUatCaseResult,
  isUatStatus,
  uatStatusCounts,
  type UatCaseResult,
  type UatStatus,
} from '../lib/uat'
import type { UatCase, UatHistoryEntry, UatItem } from '../types'
import { Alert, Button, Dialog, EmptyState, Field, StatusBadge, useToast } from './ui'
import { SectionHeader, Select } from './ui/primitives'
import { CalendarBlank, ChevronDown, ClockCounterClockwise, Inbox, SpinnerGap } from './ui/icons'
import { formatDisplayDate } from './ui/date-logic'

type Filter = 'all' | UatStatus

/** What the note dialog is for: a note, a change to Needs Fix, or a failed case. */
type Prompt =
  | { kind: 'note'; item: UatItem }
  | { kind: 'needs_fix'; item: UatItem }
  | { kind: 'fail'; item: UatItem; testCase: UatCase }

const PREVIEW_AUTHOR = 'Test Tester'

export function formatUatTime(value: string): string {
  return new Date(value).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
}

function valueLabel(value: UatHistoryEntry['fromValue']): string {
  if (isUatStatus(value)) return UAT_STATUS_LABEL[value]
  if (isUatCaseResult(value)) return UAT_CASE_RESULT_LABEL[value]
  return ''
}

/** One sentence for a history entry, for example "Alison Johnson changed the status from In Testing to Needs Fix". */
export function historySentence(entry: UatHistoryEntry): string {
  if (entry.kind === 'note') return `${entry.authorName} added a note`
  if (entry.kind === 'case_result') return `${entry.authorName} marked a test case ${valueLabel(entry.toValue)}`
  if (!entry.fromValue) return entry.toValue === 'delivered' ? `${entry.authorName} delivered this item to UAT` : `${entry.authorName} added this item as ${valueLabel(entry.toValue)}`
  return `${entry.authorName} changed the status from ${valueLabel(entry.fromValue)} to ${valueLabel(entry.toValue)}`
}

function caseSummary(cases: UatCase[]): string {
  const counts = { pass: 0, fail: 0, not_run: 0 }
  for (const testCase of cases) counts[testCase.result] += 1
  return [`${counts.pass} Pass`, `${counts.fail} Fail`, `${counts.not_run} Not Run`].join(' · ')
}

/** Local preview only: applies a change to the test data without a request. */
function previewChange(item: UatItem, change: { status?: UatStatus; note?: string | null; caseId?: string; result?: UatCaseResult }): UatItem {
  const now = new Date().toISOString()
  const base = { id: `preview-${Date.now()}`, authorName: PREVIEW_AUTHOR, authorSide: 'customer' as const, createdAt: now }
  if (change.caseId && change.result) {
    const testCase = item.cases.find((entry) => entry.id === change.caseId)
    if (!testCase) return item
    return {
      ...item,
      cases: item.cases.map((entry) => entry.id === change.caseId ? { ...entry, result: change.result!, note: change.note || null, resultBy: change.result === 'not_run' ? null : PREVIEW_AUTHOR, resultAt: change.result === 'not_run' ? null : now } : entry),
      history: [...item.history, { ...base, kind: 'case_result', body: change.note || null, fromValue: testCase.result, toValue: change.result, caseText: testCase.text }],
    }
  }
  if (change.status) {
    return { ...item, status: change.status, updatedAt: now, history: [...item.history, { ...base, kind: 'status', body: change.note || null, fromValue: item.status, toValue: change.status, caseText: null }] }
  }
  return { ...item, history: [...item.history, { ...base, kind: 'note', body: change.note || null, fromValue: null, toValue: null, caseText: null }] }
}

/**
 * The UAT tab (Rod, 2026-11-04): what Prentus delivered to the customer's UAT
 * environment, what is in testing, and the results. A verified customer can
 * change an item's status, mark test cases and add notes. A note, a change to
 * Needs Fix and a failed case go to the Prentus team as a Linear issue.
 * `preview` (local fixtures only) uses test data and makes no requests.
 */
export function UatTab({ publicId, preview = false }: { publicId: string; preview?: boolean }) {
  const toast = useToast()
  const [items, setItems] = useState<UatItem[]>(preview ? UAT_PREVIEW_ITEMS : [])
  const [canEdit, setCanEdit] = useState(preview)
  const [testData, setTestData] = useState(preview)
  const [state, setState] = useState<'loading' | 'ready' | 'error'>(preview ? 'ready' : 'loading')
  const [filter, setFilter] = useState<Filter>('all')
  const [busy, setBusy] = useState<string | null>(null)
  const [prompt, setPrompt] = useState<Prompt | null>(null)

  const load = useCallback(async () => {
    if (preview) return
    setState('loading')
    try {
      const data = await api.loadUat(publicId)
      setItems(data.items)
      setCanEdit(data.canEdit)
      setTestData(Boolean(data.testData))
      setState('ready')
    } catch {
      setState('error')
    }
  }, [preview, publicId])
  useEffect(() => { void load() }, [load])

  const counts = useMemo(() => uatStatusCounts(items), [items])
  const shown = filter === 'all' ? items : items.filter((item) => item.status === filter)

  function replace(next: UatItem) {
    setItems((current) => current.map((item) => item.id === next.id ? next : item))
  }

  async function run(key: string, previewNext: () => UatItem, request: () => Promise<{ item: UatItem }>, success: string, failure: string): Promise<boolean> {
    setBusy(key)
    try {
      replace(preview ? previewNext() : (await request()).item)
      toast.success(success)
      return true
    } catch (cause) {
      toast.error(failure, { description: cause instanceof Error ? cause.message : undefined })
      return false
    } finally {
      setBusy(null)
    }
  }

  function changeStatus(item: UatItem, status: UatStatus, note?: string) {
    return run(item.id, () => previewChange(item, { status, note }), () => api.setUatStatus(publicId, item.id, status, note), `Status Changed to ${UAT_STATUS_LABEL[status]}`, 'Status Not Changed')
  }

  function addNote(item: UatItem, note: string) {
    return run(item.id, () => previewChange(item, { note }), () => api.addUatNote(publicId, item.id, note), 'Note Sent', 'Note Not Sent')
  }

  function setResult(item: UatItem, testCase: UatCase, result: UatCaseResult, note?: string) {
    // Keyed by item: each response replaces the whole item, so one request per item at a time.
    return run(item.id, () => previewChange(item, { caseId: testCase.id, result, note }), () => api.setUatCaseResult(publicId, testCase.id, result, note), `Test Case Marked ${UAT_CASE_RESULT_LABEL[result]}`, 'Result Not Saved')
  }

  async function submitPrompt(note: string): Promise<boolean> {
    if (!prompt) return false
    const done = prompt.kind === 'note'
      ? await addNote(prompt.item, note)
      : prompt.kind === 'needs_fix'
        ? await changeStatus(prompt.item, 'needs_fix', note || undefined)
        : await setResult(prompt.item, prompt.testCase, 'fail', note || undefined)
    if (done) setPrompt(null)
    return done
  }

  return (
    <section className="standard-tab uat-tab" aria-busy={state === 'loading' || undefined}>
      <SectionHeader title="UAT" description="What Prentus delivered to your UAT environment, what is in testing, and the results. Change a status, mark each test case and add notes. Notes, Needs Fix and failed cases go straight to the Prentus team." />
      {testData && <Alert tone="warning" title="Preview With Test Data" description="This is a review page, not a customer workspace. Every item, test case and person here is test data." />}
      {!testData && state === 'ready' && !canEdit && <Alert tone="info" title="Customer View" description="Only a signed-in customer can change a status, mark a test case or add a note. You see what they see." />}
      {state === 'loading' && <div className="loading-lockup" role="status"><SpinnerGap size={18} className="spin" aria-hidden="true" />Loading UAT items</div>}
      {state === 'error' && <Alert tone="danger" title="UAT Items Did Not Load" description="Check your connection and try again." action={<Button type="button" variant="secondary" compact onClick={() => void load()}>Try Again</Button>} />}
      {state === 'ready' && (
        <>
          <div className="uat-summary" role="group" aria-label="Filter UAT items by status">
            <button type="button" className={`uat-summary-tile${filter === 'all' ? ' is-active' : ''}`} aria-pressed={filter === 'all'} onClick={() => setFilter('all')}>
              <strong>{items.length}</strong><span>All Items</span>
            </button>
            {UAT_STATUSES.map((status) => (
              <button type="button" key={status} className={`uat-summary-tile uat-summary-tile--${status}${filter === status ? ' is-active' : ''}`} aria-pressed={filter === status} onClick={() => setFilter(status)}>
                <strong>{counts[status]}</strong><span>{UAT_STATUS_LABEL[status]}</span>
              </button>
            ))}
          </div>
          <p className="ui-visually-hidden" role="status">{filter === 'all' ? `Showing all ${items.length} items.` : `Showing ${shown.length} ${UAT_STATUS_LABEL[filter]} items.`}</p>
          {shown.length === 0
            ? <EmptyState icon={Inbox} title={filter === 'all' ? 'No UAT Items Yet' : `No ${UAT_STATUS_LABEL[filter]} Items`} description={filter === 'all' ? 'Items show here when Prentus delivers them to your UAT environment.' : 'Choose another status to see more items.'} action={filter === 'all' ? undefined : <Button type="button" variant="secondary" compact onClick={() => setFilter('all')}>Show All Items</Button>} />
            : (
              <div className="uat-list">
                {shown.map((item) => (
                  <UatItemCard
                    key={item.id}
                    item={item}
                    canEdit={canEdit}
                    busy={busy}
                    onStatus={(status) => { if (status === 'needs_fix') setPrompt({ kind: 'needs_fix', item }); else void changeStatus(item, status) }}
                    onNote={() => setPrompt({ kind: 'note', item })}
                    onResult={(testCase, result) => { if (result === 'fail') setPrompt({ kind: 'fail', item, testCase }); else void setResult(item, testCase, result) }}
                  />
                ))}
              </div>
            )}
        </>
      )}
      <NoteDialog prompt={prompt} busy={busy !== null} onClose={() => setPrompt(null)} onSubmit={submitPrompt} />
    </section>
  )
}

function UatItemCard({ item, canEdit, busy, onStatus, onNote, onResult }: {
  item: UatItem
  canEdit: boolean
  busy: string | null
  onStatus: (status: UatStatus) => void
  onNote: () => void
  onResult: (testCase: UatCase, result: UatCaseResult) => void
}) {
  const headingId = useId()
  const casesId = useId()
  const historyId = useId()
  const [casesOpen, setCasesOpen] = useState(item.status === 'in_testing' && item.cases.length > 0)
  const [historyOpen, setHistoryOpen] = useState(false)
  // The select only picks a status; Save Status sends it (WCAG 3.2.2: no change on input).
  const [draftStatus, setDraftStatus] = useState<UatStatus>(item.status)
  useEffect(() => { setDraftStatus(item.status) }, [item.status])
  const itemBusy = busy === item.id

  return (
    <article className="uat-item" aria-labelledby={headingId}>
      <header className="uat-item-head">
        <div className="uat-item-title">
          <h3 id={headingId}>{item.title}</h3>
          <p className="uat-item-meta">
            {item.deliveredOn && <span><CalendarBlank size={14} aria-hidden="true" />Delivered {formatDisplayDate(item.deliveredOn)}</span>}
            {item.linearIdentifier && <span>Ticket {item.linearIdentifier}</span>}
            {item.cases.length > 0 && <span>{caseSummary(item.cases)}</span>}
          </p>
        </div>
        <StatusBadge tone={UAT_STATUS_TONE[item.status]}>{UAT_STATUS_LABEL[item.status]}</StatusBadge>
      </header>
      {item.description && <p className="uat-item-description">{item.description}</p>}

      {canEdit && (
        <div className="uat-item-actions">
          <Field label="Status" className="uat-status-field">
            <Select value={draftStatus} disabled={itemBusy} onChange={(event) => setDraftStatus(event.target.value as UatStatus)}>
              {UAT_STATUSES.map((status) => <option key={status} value={status}>{UAT_STATUS_LABEL[status]}</option>)}
            </Select>
          </Field>
          {draftStatus !== item.status && <Button type="button" compact disabled={itemBusy} onClick={() => onStatus(draftStatus)}>Save Status</Button>}
          <Button type="button" variant="secondary" compact disabled={itemBusy} onClick={onNote}>Add Note</Button>
          {itemBusy && <SpinnerGap size={16} className="spin" aria-label="Saving" />}
        </div>
      )}

      <div className="uat-item-sections">
        {item.cases.length > 0 && (
          <div className="uat-disclosure">
            <button type="button" className="uat-disclosure-toggle" aria-expanded={casesOpen} aria-controls={casesId} onClick={() => setCasesOpen((open) => !open)}>
              <ChevronDown size={16} aria-hidden="true" className={casesOpen ? 'is-open' : ''} />
              Test Cases ({item.cases.length})
            </button>
            {casesOpen && (
              <ol id={casesId} className="uat-cases">
                {item.cases.map((testCase, index) => (
                  <li key={testCase.id} className={`uat-case uat-case--${testCase.result}`}>
                    <div className="uat-case-text">
                      {testCase.area && <span className="uat-case-area">{testCase.area}</span>}
                      <p>{testCase.text}</p>
                      {testCase.note && <p className="uat-case-note"><strong>Note:</strong> {testCase.note}</p>}
                      {testCase.resultBy && testCase.resultAt && <small>Marked {UAT_CASE_RESULT_LABEL[testCase.result]} by {testCase.resultBy}, {formatUatTime(testCase.resultAt)}</small>}
                    </div>
                    {canEdit
                      ? (
                        <div className="uat-result" role="group" aria-label={`Result for test case ${index + 1}`}>
                          {UAT_CASE_RESULTS.map((result) => (
                            <button
                              type="button"
                              key={result}
                              className={`uat-result-option uat-result-option--${result}${testCase.result === result ? ' is-selected' : ''}`}
                              aria-pressed={testCase.result === result}
                              disabled={itemBusy}
                              onClick={() => { if (testCase.result !== result || result === 'fail') onResult(testCase, result) }}
                            >
                              {UAT_CASE_RESULT_LABEL[result]}
                            </button>
                          ))}
                        </div>
                      )
                      : <StatusBadge tone={testCase.result === 'pass' ? 'success' : testCase.result === 'fail' ? 'danger' : 'neutral'}>{UAT_CASE_RESULT_LABEL[testCase.result]}</StatusBadge>}
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}

        <div className="uat-disclosure">
          <button type="button" className="uat-disclosure-toggle" aria-expanded={historyOpen} aria-controls={historyId} onClick={() => setHistoryOpen((open) => !open)}>
            <ChevronDown size={16} aria-hidden="true" className={historyOpen ? 'is-open' : ''} />
            Notes and History ({item.history.length})
          </button>
          {historyOpen && (
            <ol id={historyId} className="uat-history">
              {[...item.history].reverse().map((entry) => (
                <li key={entry.id} className={`uat-history-entry uat-history-entry--${entry.kind}`}>
                  <ClockCounterClockwise size={16} aria-hidden="true" />
                  <div>
                    <p className="uat-history-line"><strong>{historySentence(entry)}</strong> <time dateTime={entry.createdAt}>{formatUatTime(entry.createdAt)}</time></p>
                    {entry.caseText && <p className="uat-history-case">Test case: {entry.caseText}</p>}
                    {entry.body && <blockquote>{entry.body}</blockquote>}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </article>
  )
}

const PROMPT_COPY: Record<Prompt['kind'], { title: string; description: string; label: string; submit: string; required: boolean }> = {
  note: { title: 'Add a Note', description: 'The Prentus team gets your note right away. Everyone on your team can see it in the item history.', label: 'Note', submit: 'Send Note', required: true },
  needs_fix: { title: 'Mark as Needs Fix', description: 'Tell the Prentus team what needs a fix. They get it right away.', label: 'What Needs a Fix', submit: 'Mark Needs Fix', required: false },
  fail: { title: 'Mark Test Case as Fail', description: 'Tell the Prentus team what happened. They get it right away.', label: 'What Happened', submit: 'Mark Fail', required: false },
}

function NoteDialog({ prompt, busy, onClose, onSubmit }: { prompt: Prompt | null; busy: boolean; onClose: () => void; onSubmit: (note: string) => Promise<boolean> }) {
  const [text, setText] = useState('')
  const [error, setError] = useState('')
  const formId = useId()
  // A case that already failed opens with its note, so sending it again changes nothing.
  useEffect(() => { setText(prompt?.kind === 'fail' ? prompt.testCase.note || '' : ''); setError('') }, [prompt])
  if (!prompt) return null
  const copy = PROMPT_COPY[prompt.kind]
  const trimmed = text.trim()

  function submit(event: FormEvent) {
    event.preventDefault()
    if (copy.required && !trimmed) { setError('Write a note before you send it.'); return }
    if (trimmed.length > UAT_LIMITS.noteChars) { setError(`Keep the note under ${UAT_LIMITS.noteChars.toLocaleString('en-US')} characters.`); return }
    setError('')
    void onSubmit(trimmed)
  }

  return (
    <Dialog
      open
      onClose={onClose}
      dismissible={!busy}
      busy={busy}
      title={copy.title}
      description={<>{copy.description} <strong>{prompt.kind === 'fail' ? prompt.testCase.text.split('\n')[0] : prompt.item.title}</strong></>}
      footer={(
        <>
          <Button type="button" variant="secondary" disabled={busy} onClick={onClose}>Cancel</Button>
          <Button type="submit" form={formId} disabled={busy}>{busy && <SpinnerGap size={16} className="spin" aria-hidden="true" />}{copy.submit}</Button>
        </>
      )}
    >
      <form id={formId} onSubmit={submit} noValidate>
        <Field label={copy.label} required={copy.required} error={error || undefined} hint={`${trimmed.length.toLocaleString('en-US')} of ${UAT_LIMITS.noteChars.toLocaleString('en-US')} characters${copy.required ? '' : '. Optional.'}`}>
          <textarea value={text} rows={5} maxLength={UAT_LIMITS.noteChars} data-autofocus onChange={(event) => setText(event.target.value)} />
        </Field>
      </form>
    </Dialog>
  )
}
